import { useEffect, useRef, useState } from 'react';
import { Button, Field, Panel, TextInput } from '../../components/ui';
import { Notice } from './pageParts';

// Local structural contract; integration owner maps this through the shared API client.
export interface AccountDeletionPreview {
  confirmation: 'DELETE';
  canDelete: boolean;
  blockers: string[];
  ownedMissions: number;
  memberships: number;
  trackedAircraft: number;
  sharedMissionPolicy: 'retain_without_account_or_billing';
}
export interface AccountDeletionResult { deleted: true; cleanup: 'complete' | 'restart_required'; files: 'complete' | 'pending' }
export interface AccountDeletionPanelProps {
  preview: () => Promise<AccountDeletionPreview>;
  deleteAccount: (input: { password: string; confirmation: 'DELETE' }) => Promise<AccountDeletionResult>;
  /** Clear browser/native session, offline outbox, personal caches and stop local location sharing. */
  onDeleted: (result: AccountDeletionResult) => void | Promise<void>;
}

export function AccountDeletionPanel({ preview, deleteAccount, onDeleted }: AccountDeletionPanelProps) {
  const [details, setDetails] = useState<AccountDeletionPreview | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState<AccountDeletionResult | null>(null);
  const [localCleanupFailed, setLocalCleanupFailed] = useState(false);
  const locked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const clearSecrets = () => { setPassword(''); setConfirmation(''); };

  async function inspect() {
    if (locked.current || result) return;
    locked.current = true; setBusy(true); setError(''); clearSecrets();
    try {
      const p = await preview();
      if (!mounted.current) return;
      setDetails(p); setOpen(true);
    } catch {
      if (mounted.current) setError('Could not load account deletion details. Please try again.');
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  async function finish(deleted: AccountDeletionResult) {
    try {
      await onDeleted(deleted);
      if (mounted.current) setLocalCleanupFailed(false);
    } catch {
      if (mounted.current) setLocalCleanupFailed(true);
    }
  }

  async function confirm() {
    if (locked.current || result || !details?.canDelete || !password || confirmation !== 'DELETE') return;
    locked.current = true; setBusy(true); setError('');
    try {
      const deleted = await deleteAccount({ password, confirmation: 'DELETE' });
      if (deleted.deleted !== true) throw new Error('Deletion was not confirmed');
      // Commit outcome precedes local cleanup. A callback failure cannot show "deletion failed".
      if (mounted.current) { setResult(deleted); setOpen(false); clearSecrets(); }
      await finish(deleted);
    } catch {
      if (mounted.current) {
        clearSecrets();
        setDetails(null); setOpen(false);
        // A dropped response may follow a committed deletion. Never assert "no changes" or
        // treat a subsequent 401 as deletion success: require a confirmed response or sign-in.
        setError('Deletion was not confirmed. Check whether you can still sign in before trying again. If you can, reload deletion details and confirm again.');
      }
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  async function retryLocalCleanup() {
    if (!result || locked.current) return;
    locked.current = true; setBusy(true);
    await finish(result);
    locked.current = false;
    if (mounted.current) setBusy(false);
  }

  return (
    <Panel title="Delete account" eyebrow="Account" className="danger-zone">
      <div className="panel-stack">
        {result ? (
          <>
            <Notice tone="success" role="status">Your account has been deleted.</Notice>
            {result.files === 'pending' && <Notice role="status">Your account is deleted. Some stored voice files are still queued for removal. This does not restore access to your deleted marks.</Notice>}
            {result.cleanup === 'restart_required' && <Notice tone="warning" role="alert">The account is deleted. Server runtime cleanup needs attention; contact Airview Studio support.</Notice>}
            {localCleanupFailed && (
              <>
                <Notice tone="warning" role="alert">Your account is deleted, but this device could not finish signing out and clearing local data.</Notice>
                <div className="panel-actions panel-actions--start">
                  <Button variant="primary" loading={busy} onClick={() => void retryLocalCleanup()}>Finish signing out</Button>
                </div>
              </>
            )}
          </>
        ) : open && details ? (
          <>
            <p>Your sessions, personal provider key, personal preferences, private aircraft watchlist and its recorded history will be removed. Location sharing and your account’s tracking will stop. Your authored marks, messages and linked crew location records will be removed.</p>
            <p className="muted">Shared shoots and coworkers’ records remain under Airview Studio’s retention policy. Your personal provider key and billing are not transferred. Organization fleets and shoots stay with remaining organization members; an admin’s organization key remains with that organization. Deletion does not guarantee that names in shared text, existing exports or backups are removed.</p>
            <dl className="deletion-counts">
              <div><dt>Shoot memberships</dt><dd className="num">{details.memberships}</dd></div>
              <div><dt>Shoots created</dt><dd className="num">{details.ownedMissions}</dd></div>
              <div><dt>Watched aircraft</dt><dd className="num">{details.trackedAircraft}</dd></div>
            </dl>
            <p className="panel-footnote">Existing exports and backups follow Airview Studio’s retention policy.</p>
            {details.blockers.map((b, i) => <Notice tone="warning" role="alert" key={i}>{b}</Notice>)}
            {(() => {
              const cancel = <Button variant="ghost" disabled={busy} onClick={() => { clearSecrets(); setOpen(false); setDetails(null); setError(''); }}>Cancel</Button>;
              return details.canDelete ? (
                <form className="panel-stack" onSubmit={e => { e.preventDefault(); void confirm(); }}>
                  <div className="settings-grid">
                    <Field label="Current password">{id => <TextInput id={id} type="password" autoComplete="current-password" maxLength={200} required disabled={busy} value={password} onChange={e => setPassword(e.target.value)} />}</Field>
                    <Field label="Type DELETE to confirm">{id => <TextInput id={id} mono autoComplete="off" spellCheck={false} maxLength={6} required disabled={busy} value={confirmation} onChange={e => setConfirmation(e.target.value)} placeholder="DELETE" />}</Field>
                  </div>
                  <div className="panel-actions">
                    {cancel}
                    <Button variant="danger" type="submit" icon="trash" loading={busy} disabled={!password || confirmation !== 'DELETE'}>Permanently delete my account</Button>
                  </div>
                </form>
              ) : <div className="panel-actions">{cancel}</div>;
            })()}
          </>
        ) : (
          <div className="settings-row">
            <p>Permanently delete your Airview Studio account. This cannot be undone.</p>
            <Button variant="danger" icon="trash" loading={busy} onClick={() => void inspect()}>Review account deletion</Button>
          </div>
        )}
        {error && <Notice tone="error" role="alert">{error}</Notice>}
      </div>
    </Panel>
  );
}
